/**
 * AI Game Builder — three-panel layout
 *
 * Chat | Files + Editor | Actions
 */

import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneDark } from 'react-syntax-highlighter/dist/esm/styles/prism';
import CodeMirror from '@uiw/react-codemirror';
import { javascript } from '@codemirror/lang-javascript';
import { oneDark as cmOneDark } from '@codemirror/theme-one-dark';
import { EditorView } from '@codemirror/view';
import { EditorSelection } from '@codemirror/state';
import { usePlayerStore } from '@/store/player.js';
import { Card } from '@playground/ui';
import { apiFetch } from '@/platform/api.js';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
}

interface FileState {
  source: string;   // last successfully saved/compiled content
  ok: boolean;
  violations: string[];
  buildError: string | null;
}

interface Asset {
  filename: string;
  contentType: string;
  size: number;
}

type GameStatus = 'draft' | 'pending_approval' | 'approved' | 'published' | 'disabled' | 'rejected';

interface SavedGame {
  id: string;
  meta: { name: string; description: string };
  status: GameStatus;
  hasBoard: boolean;
}

type ActiveFile = 'definition' | 'board' | 'assets';

// ---------------------------------------------------------------------------
// Status badge
// ---------------------------------------------------------------------------

const STATUS_STYLES: Record<GameStatus, string> = {
  draft: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300',
  pending_approval: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
  approved: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
  published: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
  disabled: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400',
  rejected: 'bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400',
};

function StatusBadge({ status }: { status: GameStatus }) {
  return (
    <span className={`rounded-full px-2.5 py-0.5 text-xs font-medium capitalize ${STATUS_STYLES[status]}`}>
      {status.replace('_', ' ')}
    </span>
  );
}

// ---------------------------------------------------------------------------
// File indicator (✓ saved+ok / ✗ saved+error / ● unsaved changes)
// ---------------------------------------------------------------------------

function FileIndicator({ file, dirty }: { file: FileState | null; dirty: boolean }) {
  if (dirty) return <span className="text-amber-400 text-xs font-bold">●</span>;
  if (!file) return null;
  if (file.ok) return <span className="text-green-500 text-xs">✓</span>;
  return <span className="text-red-500 text-xs">✗</span>;
}

// ---------------------------------------------------------------------------
// Memoized markdown message — only re-renders when content changes
// ---------------------------------------------------------------------------

const mdComponents = {
  code({ className, children, ...props }: React.HTMLAttributes<HTMLElement> & { children?: React.ReactNode }) {
    const match = /language-(\w+)/.exec(className ?? '');
    if (match) {
      return (
        <SyntaxHighlighter
          style={oneDark}
          language={match[1]}
          PreTag="div"
          customStyle={{ fontSize: '0.7rem', borderRadius: '0.4rem', margin: '0.4rem 0' }}
        >
          {String(children).replace(/\n$/, '')}
        </SyntaxHighlighter>
      );
    }
    return <code className="rounded bg-gray-200 dark:bg-gray-600 px-1 py-0.5 font-mono text-[0.7rem]" {...props}>{children}</code>;
  },
};

const MarkdownMessage = React.memo(function MarkdownMessage({ content }: { content: string }) {
  return (
    <div className="prose prose-xs dark:prose-invert max-w-none text-xs [&_pre]:!p-0 [&_pre]:!m-0 [&_pre]:!bg-transparent">
      <ReactMarkdown components={mdComponents}>{content}</ReactMarkdown>
    </div>
  );
});

// ---------------------------------------------------------------------------
// Main Builder component
// ---------------------------------------------------------------------------

export function Builder() {
  const { playerId, displayName } = usePlayerStore();

  // Chat
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [streaming, setStreaming] = useState(false);
  // Pending code from last Claude response — user pastes manually
  const [pendingDefCode, setPendingDefCode] = useState<string | null>(null);
  const [pendingBoardCode, setPendingBoardCode] = useState<string | null>(null);

  // File source (last saved/compiled state)
  const [defFile, setDefFile] = useState<FileState | null>(null);
  const [boardFile, setBoardFile] = useState<FileState | null>(null);

  // Live editor content (always editable)
  const [defEdit, setDefEdit] = useState('');
  const [boardEdit, setBoardEdit] = useState('');

  // Use refs so the SSE callback always sees latest values
  const defEditRef = useRef('');
  const boardEditRef = useRef('');
  useEffect(() => { defEditRef.current = defEdit; }, [defEdit]);
  useEffect(() => { boardEditRef.current = boardEdit; }, [boardEdit]);

  const [activeFile, setActiveFile] = useState<ActiveFile>('definition');

  // Assets
  const [assets, setAssets] = useState<Asset[]>([]);
  const [assetsLoading, setAssetsLoading] = useState(false);
  const [uploadingAsset, setUploadingAsset] = useState(false);
  const [copiedAsset, setCopiedAsset] = useState<string | null>(null);
  const assetFileRef = useRef<HTMLInputElement>(null);

  // Game state
  const [savedGame, setSavedGame] = useState<SavedGame | null>(null);
  const [saving, setSaving] = useState(false);
  const [savingBoard, setSavingBoard] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [openingRoom, setOpeningRoom] = useState(false);

  // My games tab
  const [tab, setTab] = useState<'chat' | 'my-games'>('chat');
  const [myGames, setMyGames] = useState<Array<{ id: string; status: string; meta: { name: string } }>>([]);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [loadingId, setLoadingId] = useState<string | null>(null);
  const [disablingId, setDisablingId] = useState<string | null>(null);

  const chatEndRef = useRef<HTMLDivElement>(null);
  const chatScrollRef = useRef<HTMLDivElement>(null);
  const editorWrapRef = useRef<HTMLDivElement>(null);
  const editorViewRef = useRef<EditorView | null>(null);
  // true = user scrolled up, pause auto-scroll
  const pauseScrollRef = useRef(false);
  // true = next scroll event was triggered by us (programmatic), ignore it
  const programmaticScrollRef = useRef(false);

  // Find bar
  const [findOpen, setFindOpen] = useState(false);
  const [findQuery, setFindQuery] = useState('');
  const findInputRef = useRef<HTMLInputElement>(null);

  // Dirty detection
  const defDirty = defEdit !== (defFile?.source ?? '');
  const boardDirty = boardEdit !== (boardFile?.source ?? '');

  // Auto-scroll when messages update
  useEffect(() => {
    if (pauseScrollRef.current) return;
    const el = chatScrollRef.current;
    if (!el) return;
    programmaticScrollRef.current = true;
    el.scrollTop = el.scrollHeight;
  }, [messages]);

  // Distinguish user scroll from programmatic scroll
  const handleChatScroll = useCallback(() => {
    if (programmaticScrollRef.current) {
      programmaticScrollRef.current = false;
      return;
    }
    // Real user scroll — check if they're near the bottom
    const el = chatScrollRef.current;
    if (!el) return;
    pauseScrollRef.current = el.scrollHeight - el.scrollTop - el.clientHeight > 60;
  }, []);

  useEffect(() => {
    if (!playerId) return;
    apiFetch(`/api/ai-builder/my-games?playerId=${playerId}`)
      .then((r) => r.json() as Promise<{ games: Array<{ id: string; status: string; meta: { name: string } }> }>)
      .then((d) => setMyGames(d.games ?? []))
      .catch(() => {});
  }, [playerId, savedGame]);

  // Load assets when switching to assets tab
  const loadAssets = useCallback(async () => {
    if (!savedGame?.id) return;
    setAssetsLoading(true);
    try {
      const res = await apiFetch(`/api/ai-builder/game/${savedGame.id}/assets`);
      const data = await res.json() as { assets: Asset[] };
      setAssets(data.assets ?? []);
    } finally {
      setAssetsLoading(false);
    }
  }, [savedGame?.id]);

  useEffect(() => {
    if (activeFile === 'assets') void loadAssets();
  }, [activeFile, loadAssets]);

  // -------------------------------------------------------------------------
  // File switching with unsaved-changes guard
  // -------------------------------------------------------------------------

  function switchFile(to: ActiveFile) {
    setActiveFile(to);
  }

  // -------------------------------------------------------------------------
  // Find in editor
  // -------------------------------------------------------------------------

  const currentEditValue = activeFile === 'definition' ? defEdit : boardEdit;

  const findMatches = useMemo(() => {
    if (!findQuery) return [];
    const text = currentEditValue;
    const q = findQuery.toLowerCase();
    const indices: number[] = [];
    let i = text.toLowerCase().indexOf(q);
    while (i !== -1) {
      indices.push(i);
      i = text.toLowerCase().indexOf(q, i + 1);
    }
    return indices;
  }, [findQuery, currentEditValue]);

  const [findIndex, setFindIndex] = useState(0);

  function openFind() {
    setFindOpen(true);
    setTimeout(() => findInputRef.current?.focus(), 50);
  }

  function closeFind() {
    setFindOpen(false);
    setFindQuery('');
    editorViewRef.current?.focus();
  }

  function jumpToMatch(idx: number) {
    if (findMatches.length === 0) return;
    const clamped = (idx + findMatches.length) % findMatches.length;
    setFindIndex(clamped);
    const start = findMatches[clamped]!;
    const end = start + findQuery.length;
    const view = editorViewRef.current;
    if (view) {
      view.dispatch({
        selection: EditorSelection.single(start, end),
        effects: EditorView.scrollIntoView(start, { y: 'center' }),
      });
      // Keep focus on the find input, not the editor
      requestAnimationFrame(() => findInputRef.current?.focus());
    }
  }

  // Reset findIndex when matches change
  useEffect(() => { setFindIndex(0); }, [findQuery, activeFile]);

  // Scroll to first match when query changes
  useEffect(() => {
    if (findOpen && findMatches.length > 0) jumpToMatch(0);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [findMatches, findOpen]);

  // -------------------------------------------------------------------------
  // Paste pending code into editor
  // -------------------------------------------------------------------------

  function pasteDefinition() {
    if (!pendingDefCode) return;
    setDefEdit(pendingDefCode);
    setDefFile({ source: pendingDefCode, ok: false, violations: [], buildError: null });
    setActiveFile('definition');
    setPendingDefCode(null);
  }

  function pasteBoard() {
    if (!pendingBoardCode) return;
    setBoardEdit(pendingBoardCode);
    setBoardFile({ source: pendingBoardCode, ok: false, violations: [], buildError: null });
    setActiveFile('board');
    setPendingBoardCode(null);
  }

  // -------------------------------------------------------------------------
  // Chat / SSE
  // -------------------------------------------------------------------------

  async function sendMessage() {
    if (!input.trim() || streaming || !playerId) return;

    const userMsg: ChatMessage = { role: 'user', content: input.trim() };
    const newHistory = [...messages, userMsg];
    setMessages([...newHistory, { role: 'assistant', content: '' }]);
    setInput('');
    setStreaming(true);
    pauseScrollRef.current = false;
    setPendingDefCode(null);
    setPendingBoardCode(null);

    try {
      const res = await apiFetch('/api/ai-builder/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          playerId,
          message: userMsg.content,
          history: messages,
          currentCode: {
            definition: defEditRef.current || undefined,
            board: boardEditRef.current || undefined,
          },
        }),
      });

      if (!res.ok || !res.body) throw new Error('Server error');

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let assistantText = '';
      let sseBuffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        sseBuffer += decoder.decode(value, { stream: true });
        const parts = sseBuffer.split('\n\n');
        sseBuffer = parts.pop() ?? '';

        for (const part of parts) {
          for (const line of part.split('\n')) {
            if (!line.startsWith('data: ')) continue;
            const raw = line.slice(6).trim();
            if (!raw || raw === '[DONE]') continue;
            try {
              const event = JSON.parse(raw) as {
                type: string; text?: string;
                ok?: boolean; violations?: string[]; buildError?: string; code?: string;
              };

              if (event.type === 'delta' && event.text) {
                assistantText += event.text;
                setMessages((prev) => {
                  const updated = [...prev];
                  updated[updated.length - 1] = { role: 'assistant', content: assistantText };
                  return updated;
                });
              } else if (event.type === 'code') {
                setPendingDefCode(event.code ?? '');
              } else if (event.type === 'code_board') {
                setPendingBoardCode(event.code ?? '');
              }
            } catch { /* skip malformed */ }
          }
        }
      }
    } catch (err) {
      setMessages((prev) => {
        const updated = [...prev];
        updated[updated.length - 1] = { role: 'assistant', content: `Error: ${String(err)}` };
        return updated;
      });
    } finally {
      setStreaming(false);
    }
  }

  // -------------------------------------------------------------------------
  // Save definition.ts
  // -------------------------------------------------------------------------

  async function saveDefinition() {
    if (!defEdit.trim() || !playerId || saving) return;
    setSaving(true);
    try {
      const res = await apiFetch('/api/ai-builder/save', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ playerId, code: defEdit }),
      });
      const data = await res.json() as {
        id?: string; meta?: { name: string; description: string };
        error?: string; violations?: string[]; buildError?: string;
      };
      if (!res.ok) {
        setDefFile({
          source: defEdit,
          ok: false,
          violations: data.violations ?? (data.error ? [data.error] : ['Save failed']),
          buildError: data.buildError ?? null,
        });
        return;
      }
      setDefFile({ source: defEdit, ok: true, violations: [], buildError: null });
      setSavedGame((g) => ({
        id: data.id!,
        meta: data.meta!,
        status: g?.id === data.id ? (g?.status ?? 'draft') : 'draft',
        hasBoard: g?.id === data.id ? (g?.hasBoard ?? false) : false,
      }));
    } finally {
      setSaving(false);
    }
  }

  // -------------------------------------------------------------------------
  // Save board.tsx
  // -------------------------------------------------------------------------

  async function saveBoard() {
    if (!boardEdit.trim() || !playerId || savingBoard) return;
    if (!savedGame) {
      setBoardFile({
        source: boardEdit,
        ok: false,
        violations: ['Save definition.ts first — the board needs an associated game.'],
        buildError: null,
      });
      return;
    }
    setSavingBoard(true);
    try {
      const res = await apiFetch('/api/ai-builder/save-board', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ playerId, gameId: savedGame.id, boardCode: boardEdit }),
      });
      const data = await res.json() as { ok?: boolean; error?: string; violations?: string[]; buildError?: string };
      if (!res.ok) {
        const isNotFound = res.status === 404;
        setBoardFile({
          source: boardEdit,
          ok: false,
          violations: data.violations ?? (data.error
            ? [isNotFound ? 'Game not found — save definition.ts first to create the game.' : data.error]
            : ['Save failed']),
          buildError: data.buildError ?? null,
        });
        return;
      }
      setBoardFile({ source: boardEdit, ok: true, violations: [], buildError: null });
      setSavedGame((g) => g && { ...g, hasBoard: true });
    } finally {
      setSavingBoard(false);
    }
  }

  // -------------------------------------------------------------------------
  // Submit for review
  // -------------------------------------------------------------------------

  async function submitGame() {
    if (!savedGame || !playerId || submitting) return;
    setSubmitting(true);
    try {
      const res = await apiFetch('/api/ai-builder/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ playerId, gameId: savedGame.id }),
      });
      const data = await res.json() as { status?: string; aiReview?: { safe: boolean; notes: string } };
      if (res.ok) {
        const newStatus = (data.status as GameStatus) ?? 'pending_approval';
        setSavedGame((g) => g && { ...g, status: newStatus });
        if (data.aiReview && !data.aiReview.safe) {
          alert(`AI scan flagged this game as potentially unsafe:\n${data.aiReview.notes}`);
        }
      } else {
        alert('Submit failed');
      }
    } finally {
      setSubmitting(false);
    }
  }

  // -------------------------------------------------------------------------
  // Open in Room (for testing)
  // -------------------------------------------------------------------------

  async function openInRoom(gameIdOverride?: string) {
    const gid = gameIdOverride ?? savedGame?.id;
    if (!gid || !playerId || openingRoom) return;
    setOpeningRoom(true);
    try {
      const res = await apiFetch('/api/rooms', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          gameId: gid,
          playerId,
          displayName: displayName ?? 'Builder',
          isPrivate: true,
        }),
      });
      const data = await res.json() as { roomId?: string; token?: string; message?: string };
      if (!res.ok) { alert(data.message ?? 'Failed to create room'); return; }
      if (data.roomId) window.open(`/room/${data.roomId}?token=${encodeURIComponent(data.token ?? '')}`, '_blank');
    } finally {
      setOpeningRoom(false);
    }
  }

  // -------------------------------------------------------------------------
  // Assets
  // -------------------------------------------------------------------------

  async function uploadAsset(file: File) {
    if (!savedGame?.id || !playerId || uploadingAsset) return;
    if (file.size > 512 * 1024) { alert('Image must be under 512 KB'); return; }

    setUploadingAsset(true);
    try {
      const arrayBuffer = await file.arrayBuffer();
      const bytes = new Uint8Array(arrayBuffer);
      let binary = '';
      for (let i = 0; i < bytes.byteLength; i++) binary += String.fromCharCode(bytes[i]!);
      const dataBase64 = btoa(binary);

      const res = await apiFetch(`/api/ai-builder/game/${savedGame.id}/assets`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ playerId, filename: file.name, contentType: file.type, dataBase64 }),
      });
      if (!res.ok) {
        const data = await res.json() as { error?: string };
        alert(data.error ?? 'Upload failed');
        return;
      }
      await loadAssets();
    } finally {
      setUploadingAsset(false);
      if (assetFileRef.current) assetFileRef.current.value = '';
    }
  }

  async function deleteAsset(filename: string) {
    if (!savedGame?.id || !playerId) return;
    if (!window.confirm(`Delete "${filename}"?`)) return;
    await apiFetch(`/api/ai-builder/game/${savedGame.id}/assets/${encodeURIComponent(filename)}`, {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ playerId }),
    });
    await loadAssets();
  }

  // -------------------------------------------------------------------------
  // My Games actions
  // -------------------------------------------------------------------------

  async function loadGame(gameId: string) {
    if (!playerId || loadingId) return;
    setLoadingId(gameId);
    try {
      const res = await apiFetch(`/api/ai-builder/game/${gameId}?playerId=${playerId}`);
      if (!res.ok) { alert('Failed to load game'); return; }
      const data = await res.json() as {
        id: string; status: string;
        meta: { name: string; description: string };
        source_code: string; board_source?: string | null;
      };
      const defSrc = data.source_code ?? '';
      const boardSrc = data.board_source ?? '';
      setDefEdit(defSrc);
      setBoardEdit(boardSrc);
      setDefFile({ source: defSrc, ok: true, violations: [], buildError: null });
      setBoardFile(boardSrc ? { source: boardSrc, ok: true, violations: [], buildError: null } : null);
      setSavedGame({
        id: data.id, meta: data.meta, status: data.status as GameStatus,
        hasBoard: !!boardSrc,
      });
      setTab('chat');
      setActiveFile('definition');
    } finally {
      setLoadingId(null);
    }
  }

  async function deleteGame(gameId: string, gameName: string) {
    if (!playerId || deletingId) return;
    if (!window.confirm(`Delete "${gameName}"? This cannot be undone.`)) return;
    setDeletingId(gameId);
    try {
      const res = await apiFetch(`/api/ai-builder/game/${gameId}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ playerId }),
      });
      if (!res.ok) {
        const data = await res.json() as { error?: string };
        alert(data.error ?? 'Delete failed');
        return;
      }
      setMyGames((prev) => prev.filter((g) => g.id !== gameId));
      if (savedGame?.id === gameId) setSavedGame(null);
    } finally {
      setDeletingId(null);
    }
  }

  async function disableGame(gameId: string, gameName: string) {
    if (!playerId || disablingId) return;
    if (!window.confirm(`Take "${gameName}" offline?`)) return;
    setDisablingId(gameId);
    try {
      const res = await apiFetch(`/api/ai-builder/game/${gameId}/disable`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ playerId }),
      });
      if (!res.ok) {
        const data = await res.json() as { error?: string };
        alert(data.error ?? 'Failed to take down game');
        return;
      }
      setMyGames((prev) => prev.map((g) => g.id === gameId ? { ...g, status: 'disabled' } : g));
      if (savedGame?.id === gameId) setSavedGame((g) => g && { ...g, status: 'disabled' });
    } finally {
      setDisablingId(null);
    }
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); void sendMessage(); }
  }

  // -------------------------------------------------------------------------
  // Derived state
  // -------------------------------------------------------------------------

  const currentFile = activeFile === 'definition' ? defFile : boardFile;
  const currentDirty = activeFile === 'definition' ? defDirty : boardDirty;

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  return (
    <main className="flex h-dvh flex-col bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="border-b border-gray-200 bg-white pl-[max(1rem,var(--safe-l))] pr-[max(1rem,var(--safe-r))] pt-[calc(0.625rem+var(--safe-t))] pb-2.5 dark:border-gray-700 dark:bg-gray-800">
        <div className="mx-auto flex max-w-full items-center justify-between gap-3">
          <h1 className="text-base font-bold text-gray-900 dark:text-gray-100 shrink-0">AI Game Builder</h1>
          <div className="flex gap-2">
            <button
              onClick={() => setTab('chat')}
              className={`rounded-lg px-3 py-1.5 text-sm font-medium transition-colors ${tab === 'chat' ? 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400' : 'text-gray-500 hover:text-gray-700 dark:text-gray-400'}`}
            >Builder</button>
            <button
              onClick={() => setTab('my-games')}
              className={`rounded-lg px-3 py-1.5 text-sm font-medium transition-colors ${tab === 'my-games' ? 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400' : 'text-gray-500 hover:text-gray-700 dark:text-gray-400'}`}
            >My Games {myGames.length > 0 && `(${myGames.length})`}</button>
          </div>
        </div>
      </div>

      {tab === 'my-games' ? (
        /* ── My Games tab ── */
        <div className="mx-auto w-full max-w-2xl p-4">
          <div className="space-y-3">
            {myGames.length === 0 && (
              <p className="mt-8 text-center text-sm text-gray-500 dark:text-gray-400">
                No games yet. Start a conversation to build your first game!
              </p>
            )}
            {myGames.map((g) => (
              <Card key={g.id}>
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-medium text-gray-900 dark:text-gray-100">{g.meta.name}</p>
                    <p className="mt-0.5 truncate font-mono text-xs text-gray-500 dark:text-gray-400">{g.id}</p>
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    <StatusBadge status={g.status as GameStatus} />
                    <button
                      onClick={() => void loadGame(g.id)}
                      disabled={!!loadingId || !!deletingId}
                      className="rounded-lg border border-indigo-300 px-2.5 py-1 text-xs font-medium text-indigo-600 hover:bg-indigo-50 dark:border-indigo-700 dark:text-indigo-400 disabled:opacity-40 transition-colors"
                    >{loadingId === g.id ? '…' : 'Edit'}</button>
                    <button
                      onClick={() => void openInRoom(g.id)}
                      disabled={openingRoom}
                      className="rounded-lg border border-gray-300 px-2.5 py-1 text-xs font-medium text-gray-600 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-400 disabled:opacity-40 transition-colors"
                    >{openingRoom ? '…' : 'Open →'}</button>
                    {['approved', 'published'].includes(g.status) && (
                      <button
                        onClick={() => void disableGame(g.id, g.meta.name)}
                        disabled={!!disablingId || !!loadingId}
                        className="rounded-lg border border-orange-300 px-2.5 py-1 text-xs font-medium text-orange-600 hover:bg-orange-50 dark:border-orange-700 dark:text-orange-400 disabled:opacity-40 transition-colors"
                      >{disablingId === g.id ? '…' : 'Take Down'}</button>
                    )}
                    {!['published', 'approved', 'disabled'].includes(g.status) && (
                      <button
                        onClick={() => void deleteGame(g.id, g.meta.name)}
                        disabled={!!deletingId || !!loadingId}
                        className="rounded-lg border border-red-300 px-2.5 py-1 text-xs font-medium text-red-600 hover:bg-red-50 dark:border-red-700 dark:text-red-400 disabled:opacity-40 transition-colors"
                      >{deletingId === g.id ? '…' : 'Delete'}</button>
                    )}
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      ) : (
        /* ── Builder tab — toolbar + two-column layout ── */
        <>
          {/* Top toolbar: game name/status + action buttons */}
          <div className="shrink-0 border-b border-gray-200 bg-white px-4 py-2 dark:border-gray-700 dark:bg-gray-800">
            <div className="flex items-center gap-3 overflow-x-auto">
              {/* Game name + status */}
              <div className="flex items-center gap-2 shrink-0 mr-1">
                {savedGame ? (
                  <>
                    <span className="text-sm font-semibold text-gray-900 dark:text-gray-100 max-w-[180px] truncate">{savedGame.meta.name}</span>
                    <span className="font-mono text-xs text-gray-500 dark:text-gray-400 max-w-[120px] truncate">{savedGame.id}</span>
                    <StatusBadge status={savedGame.status} />
                  </>
                ) : (
                  <span className="text-sm text-gray-500 dark:text-gray-400">No game saved yet</span>
                )}
              </div>

              <div className="h-5 w-px bg-gray-200 dark:bg-gray-700 shrink-0" />

              {/* Save current file */}
              {activeFile !== 'assets' && (
                <button
                  onClick={() => {
                    if (activeFile === 'definition') void saveDefinition();
                    else void saveBoard();
                  }}
                  disabled={
                    activeFile === 'definition' ? (!defEdit.trim() || saving) :
                    activeFile === 'board' ? (savingBoard) : true
                  }
                  title={activeFile === 'board' && !savedGame ? 'Save definition.ts first' : undefined}
                  className="shrink-0 rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-indigo-700 disabled:opacity-40 active:scale-95 transition-all"
                >
                  {(saving && activeFile === 'definition') || (savingBoard && activeFile === 'board') ? 'Saving…' : 'Save'}
                </button>
              )}

              {/* Open in Room */}
              <button
                onClick={() => void openInRoom()}
                disabled={!savedGame || openingRoom}
                className="shrink-0 rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700 disabled:opacity-40 active:scale-95 transition-all"
              >{openingRoom ? 'Opening…' : 'Open in Room →'}</button>

              {/* Submit for Review */}
              <button
                onClick={() => void submitGame()}
                disabled={!savedGame || savedGame.status !== 'draft' || submitting}
                className="shrink-0 rounded-lg border border-indigo-600 px-3 py-1.5 text-xs font-semibold text-indigo-600 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 disabled:opacity-40 active:scale-95 transition-all"
              >{submitting ? 'Submitting…' : 'Submit for Review'}</button>

              {/* Inline status messages */}
              {savedGame?.status === 'pending_approval' && (
                <span className="shrink-0 text-xs text-gray-500 dark:text-gray-400">Pending admin approval.</span>
              )}
              {savedGame?.status === 'rejected' && (
                <span className="shrink-0 text-xs text-red-500 dark:text-red-400">Rejected. Edit and resubmit.</span>
              )}
              {savedGame?.status === 'published' && (
                <span className="shrink-0 text-xs font-medium text-green-600 dark:text-green-400">✓ Live!</span>
              )}
            </div>
          </div>

          {/* Two-column main area */}
          <div className="flex flex-1 min-h-0 gap-3 p-3">

            {/* LEFT — Files + Editor (larger) */}
            <div className="flex flex-[3] min-w-0 min-h-0 flex-col rounded-xl border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800">

              {/* File tabs */}
              <div className="border-b border-gray-200 dark:border-gray-700 flex text-xs shrink-0">
                {/* definition.ts */}
                <button
                  onClick={() => switchFile('definition')}
                  className={[
                    'flex flex-1 items-center gap-1 px-3 py-2 transition-colors',
                    activeFile === 'definition'
                      ? 'bg-indigo-50 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300 border-b-2 border-indigo-500'
                      : 'text-gray-600 hover:bg-gray-50 dark:text-gray-400 dark:hover:bg-gray-700',
                  ].join(' ')}
                >
                  <span className="font-mono truncate">definition.ts</span>
                  {defDirty && <span className="text-amber-500 font-bold ml-0.5">*</span>}
                  {!defDirty && defFile && !defFile.ok && <span className="text-red-500 ml-0.5">✗</span>}
                </button>

                {/* board.tsx */}
                <button
                  onClick={() => switchFile('board')}
                  className={[
                    'flex flex-1 items-center gap-1 px-3 py-2 transition-colors border-l border-gray-200 dark:border-gray-700',
                    activeFile === 'board'
                      ? 'bg-indigo-50 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300 border-b-2 border-indigo-500'
                      : 'text-gray-600 hover:bg-gray-50 dark:text-gray-400 dark:hover:bg-gray-700',
                  ].join(' ')}
                >
                  <span className="font-mono truncate">board.tsx</span>
                  {boardDirty && <span className="text-amber-500 font-bold ml-0.5">*</span>}
                  {!boardDirty && boardFile && !boardFile.ok && <span className="text-red-500 ml-0.5">✗</span>}
                </button>

                {/* assets */}
                <button
                  onClick={() => void switchFile('assets')}
                  className={[
                    'flex items-center gap-1 px-3 py-2 transition-colors border-l border-gray-200 dark:border-gray-700',
                    activeFile === 'assets'
                      ? 'bg-indigo-50 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300 border-b-2 border-indigo-500'
                      : 'text-gray-600 hover:bg-gray-50 dark:text-gray-400 dark:hover:bg-gray-700',
                  ].join(' ')}
                >
                  🖼 <span className="hidden sm:inline">Assets</span>
                </button>
              </div>

              {/* Content area */}
              {activeFile === 'assets' ? (
                /* ── Assets tab ── */
                <div className="flex flex-1 min-h-0 flex-col p-3 gap-3">
                  <div className="flex items-center justify-between shrink-0">
                    <p className="text-xs font-medium text-gray-600 dark:text-gray-400">
                      {savedGame ? 'Images for your board UI' : 'Save a game first to upload assets'}
                    </p>
                    {savedGame && (
                      <label className={[
                        'cursor-pointer rounded-lg px-3 py-1.5 text-xs font-semibold text-white transition-colors',
                        uploadingAsset ? 'bg-indigo-400' : 'bg-indigo-600 hover:bg-indigo-700',
                      ].join(' ')}>
                        {uploadingAsset ? '…' : '↑ Upload'}
                        <input
                          ref={assetFileRef}
                          type="file"
                          accept="image/jpeg,image/png,image/gif,image/webp"
                          className="hidden"
                          disabled={uploadingAsset}
                          onChange={(e) => { const f = e.target.files?.[0]; if (f) void uploadAsset(f); }}
                        />
                      </label>
                    )}
                  </div>

                  <div className="flex-1 min-h-0 overflow-y-auto">
                    {assetsLoading ? (
                      <p className="text-xs text-gray-500 dark:text-gray-400">Loading…</p>
                    ) : assets.length === 0 ? (
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {savedGame
                          ? 'No assets yet. Upload JPEG, PNG, or GIF images (max 512 KB each).'
                          : 'Save your game definition first.'}
                      </p>
                    ) : (
                      <div className="space-y-2">
                        {assets.map((asset) => {
                          const url = `/api/ai-builder/game/${savedGame!.id}/assets/${encodeURIComponent(asset.filename)}`;
                          return (
                            <div key={asset.filename} className="flex items-center gap-2 rounded-lg border border-gray-200 dark:border-gray-700 p-2">
                              <img src={url} alt={asset.filename} className="w-10 h-10 object-cover rounded shrink-0" />
                              <div className="flex-1 min-w-0">
                                <p className="text-xs font-medium text-gray-800 dark:text-gray-200 truncate">{asset.filename}</p>
                                <p className="text-xs text-gray-500 dark:text-gray-400">{(asset.size / 1024).toFixed(1)} KB</p>
                              </div>
                              <button
                                onClick={() => {
                                  void navigator.clipboard.writeText(url).then(() => {
                                    setCopiedAsset(asset.filename);
                                    setTimeout(() => setCopiedAsset(null), 1500);
                                  });
                                }}
                                title="Copy URL"
                                className={[
                                  'shrink-0 rounded px-2 py-1 text-xs border transition-colors',
                                  copiedAsset === asset.filename
                                    ? 'border-green-400 bg-green-50 text-green-600 dark:border-green-600 dark:bg-green-900/20 dark:text-green-400'
                                    : 'border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700',
                                ].join(' ')}
                              >{copiedAsset === asset.filename ? '✓ Copied' : 'Copy URL'}</button>
                              <button
                                onClick={() => void deleteAsset(asset.filename)}
                                title="Delete"
                                className="shrink-0 text-gray-300 hover:text-red-500 dark:text-gray-600 dark:hover:text-red-400 transition-colors text-sm"
                              >✕</button>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  {savedGame && (
                    <div className="shrink-0 rounded-lg bg-gray-50 dark:bg-gray-700/40 p-2">
                      <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed">
                        Use in board.tsx:<br />
                        <code className="font-mono text-[10px] break-all text-gray-600 dark:text-gray-300">
                          /api/ai-builder/game/{savedGame.id}/assets/filename.png
                        </code>
                      </p>
                    </div>
                  )}
                </div>
              ) : (
                /* ── Code editor (definition.ts / board.tsx) ── */
                <div className="flex flex-1 min-h-0 flex-col">
                  {/* Error banner (when saved file has issues and no pending edits) */}
                  {!currentDirty && currentFile && !currentFile.ok && (
                    <div className="shrink-0 border-b border-red-200 bg-red-50 dark:border-red-900/30 dark:bg-red-900/10 px-3 py-2">
                      {currentFile.violations.map((v, i) => (
                        <p key={i} className="text-xs text-red-600 dark:text-red-400">• {v}</p>
                      ))}
                      {currentFile.buildError && (
                        <p className="mt-1 font-mono text-[10px] text-red-600 dark:text-red-400 break-all">{currentFile.buildError}</p>
                      )}
                    </div>
                  )}

                  {/* Find bar */}
                  {findOpen && (
                    <div className="shrink-0 flex items-center gap-2 border-b border-indigo-200 dark:border-indigo-700 bg-indigo-50 dark:bg-indigo-950/30 px-3 py-1.5">
                      <input
                        ref={findInputRef}
                        value={findQuery}
                        onChange={(e) => setFindQuery(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Escape') closeFind();
                          else if (e.key === 'Enter') { e.preventDefault(); jumpToMatch(e.shiftKey ? findIndex - 1 : findIndex + 1); }
                        }}
                        placeholder="Find…"
                        className="flex-1 min-w-0 rounded border border-indigo-300 dark:border-indigo-600 bg-white dark:bg-gray-800 px-2 py-0.5 text-xs font-mono focus:outline-none focus-visible:ring-1 focus-visible:ring-indigo-500"
                      />
                      <span className="shrink-0 text-xs text-indigo-600 dark:text-indigo-400 tabular-nums">
                        {findMatches.length === 0
                          ? (findQuery ? '0 / 0' : '')
                          : `${findIndex + 1} / ${findMatches.length}`}
                      </span>
                      <button onClick={() => jumpToMatch(findIndex - 1)} disabled={findMatches.length === 0} className="rounded p-0.5 hover:bg-indigo-100 dark:hover:bg-indigo-800 disabled:opacity-30" title="Previous (Shift+Enter)">▲</button>
                      <button onClick={() => jumpToMatch(findIndex + 1)} disabled={findMatches.length === 0} className="rounded p-0.5 hover:bg-indigo-100 dark:hover:bg-indigo-800 disabled:opacity-30" title="Next (Enter)">▼</button>
                      <button onClick={closeFind} className="rounded p-0.5 hover:bg-indigo-100 dark:hover:bg-indigo-800 text-gray-500" title="Close (Esc)">✕</button>
                    </div>
                  )}

                  {/* Status bar */}
                  <div className="shrink-0 flex items-center justify-between border-b border-gray-100 dark:border-gray-700 px-3 py-1.5 text-xs">
                    {currentDirty ? (
                      <span className="text-amber-500">● unsaved changes</span>
                    ) : currentFile?.ok ? (
                      <span className="text-green-600 dark:text-green-400">✓ saved</span>
                    ) : currentFile ? (
                      <span className="text-red-500">✗ has issues</span>
                    ) : (
                      <span className="text-gray-500 dark:text-gray-400">empty</span>
                    )}
                    <span className="text-gray-500 dark:text-gray-400">
                      {activeFile === 'definition' ? 'definition.ts' : 'board.tsx'}
                    </span>
                  </div>

                  {/* Code editor */}
                  <div ref={editorWrapRef} className="flex-1 min-h-0 overflow-auto" onKeyDown={(e) => { if ((e.ctrlKey || e.metaKey) && e.key === 'f') { e.preventDefault(); openFind(); } }}>
                    <CodeMirror
                      value={currentEditValue}
                      extensions={[javascript({ typescript: true, jsx: true })]}
                      theme={cmOneDark}
                      onCreateEditor={(view) => { editorViewRef.current = view; }}
                      onChange={(val) => {
                        if (activeFile === 'definition') setDefEdit(val);
                        else setBoardEdit(val);
                      }}
                      basicSetup={{
                        lineNumbers: true,
                        foldGutter: false,
                        dropCursor: false,
                        allowMultipleSelections: false,
                        indentOnInput: true,
                        bracketMatching: true,
                        closeBrackets: true,
                        autocompletion: false,
                        highlightActiveLine: true,
                        searchKeymap: false,
                      }}
                      style={{ fontSize: '12px', height: '100%' }}
                      height="100%"
                      placeholder={activeFile === 'definition'
                        ? 'Paste or write your definition.ts here, or ask Claude to generate it…'
                        : 'Paste or write your board.tsx here, or ask Claude to generate it…'}
                    />
                  </div>
                </div>
              )}
            </div>

            {/* RIGHT — Chat (smaller) */}
            <div className="flex flex-[2] min-w-0 min-h-0 flex-col rounded-xl border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800">
              {/* Messages */}
              <div ref={chatScrollRef} onScroll={handleChatScroll} className="flex-1 min-h-0 overflow-y-auto p-3 space-y-3">
                {messages.length === 0 && (
                  <div className="flex h-full items-center justify-center">
                    <div className="text-center text-gray-500 dark:text-gray-400">
                      <p className="mb-3 text-3xl">🎮</p>
                      <p className="text-sm font-medium">Describe the game you want to build</p>
                      <p className="mt-1 text-xs">e.g. "Make a number guessing game for 2–6 players"</p>
                      <p className="mt-0.5 text-xs opacity-70">You can also ask for a custom board UI</p>
                    </div>
                  </div>
                )}
                {messages.map((msg, i) => (
                  <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                    <div className={[
                      'max-w-[90%] rounded-2xl px-3 py-2 text-sm',
                      msg.role === 'user'
                        ? 'bg-indigo-600 text-white'
                        : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-100',
                    ].join(' ')}>
                      {msg.role === 'user' ? (
                        <p className="whitespace-pre-wrap break-words text-xs">{msg.content}</p>
                      ) : i === messages.length - 1 && streaming ? (
                        // While streaming: plain text, no expensive markdown parsing
                        <pre className="whitespace-pre-wrap font-sans break-words text-xs">
                          {msg.content}
                          <span className="inline-block h-3 w-1 animate-pulse rounded-sm bg-gray-500 dark:bg-gray-400 ml-0.5" />
                        </pre>
                      ) : (
                        // Completed message: memoized markdown with syntax highlighting
                        <MarkdownMessage content={msg.content} />
                      )}
                    </div>
                  </div>
                ))}
                <div ref={chatEndRef} />
              </div>

              {/* Paste buttons — shown when Claude has generated code */}
              {(pendingDefCode || pendingBoardCode) && (
                <div className="flex gap-2 px-2.5 pt-2 pb-1">
                  {pendingDefCode && (
                    <button
                      onClick={pasteDefinition}
                      className="flex items-center gap-1.5 rounded-lg bg-indigo-50 border border-indigo-200 px-3 py-1.5 text-xs font-semibold text-indigo-700 hover:bg-indigo-100 active:scale-95 transition-all dark:bg-indigo-900/30 dark:border-indigo-700 dark:text-indigo-300"
                    >
                      ↙ Paste Definition
                    </button>
                  )}
                  {pendingBoardCode && (
                    <button
                      onClick={pasteBoard}
                      className="flex items-center gap-1.5 rounded-lg bg-purple-50 border border-purple-200 px-3 py-1.5 text-xs font-semibold text-purple-700 hover:bg-purple-100 active:scale-95 transition-all dark:bg-purple-900/30 dark:border-purple-700 dark:text-purple-300"
                    >
                      ↙ Paste Board
                    </button>
                  )}
                </div>
              )}

              {/* Input */}
              <div className="border-t border-gray-200 p-2.5 dark:border-gray-700">
                <div className="flex gap-2">
                  <textarea
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={handleKeyDown}
                    disabled={streaming}
                    placeholder="Ask Claude… (Enter to send)"
                    rows={2}
                    className="flex-1 resize-none rounded-xl border border-gray-300 px-3 py-2 text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100 dark:placeholder-gray-500 disabled:opacity-50"
                  />
                  <button
                    onClick={() => void sendMessage()}
                    disabled={streaming || !input.trim()}
                    className="self-end rounded-xl bg-indigo-600 px-3 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-40 active:scale-95 transition-all"
                  >{streaming ? '…' : 'Send'}</button>
                </div>
              </div>
            </div>

          </div>
        </>
      )}
    </main>
  );
}
